import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  FolderKanban,
  Calendar,
  DollarSign,
  CreditCard,
  TrendingUp,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Eye,
  Loader2,
  Building2,
  Users,
  Receipt,
  Coins,
  FileText,
  Home,
} from 'lucide-react';
import { cn } from '../lib/utils';
import pb from '../lib/pocketbaseClient';

// ─── Sidebar Navigation ──────────────────────────────────────────
const NAV_ITEMS = [
  { id: 'dashboard', path: '/partner/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'projects', path: '/partner/projects', label: 'Projects', icon: FolderKanban },
  { id: 'attendance', path: '/partner/attendance', label: 'Labour Attendance', icon: Calendar },
  { id: 'expenses', path: '/partner/expenses', label: 'Project Expenses', icon: DollarSign },
  { id: 'vendor-payments', path: '/partner/vendor-payments', label: 'Vendor Bills & Payments', icon: CreditCard },
  { id: 'client-payments', path: '/partner/client-payments', label: 'Client Payment Received', icon: Receipt },
  { id: 'investments', path: '/partner/investments', label: 'Fund / Investments', icon: Coins },
  { id: 'reports', path: '/partner/reports', label: 'Reports & P&L', icon: BarChart3 },
  { id: 'settings', path: '/partner/settings', label: 'Settings', icon: Settings },
];

// ─── Sidebar Component ──────────────────────────────────────────
const PartnerSidebar = ({ partnerName, role, assignedSites }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true);

  const isActive = (path) => {
    if (path === '/partner/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside className="w-64 bg-slate-900 text-slate-300 h-full overflow-y-auto flex-shrink-0 border-r border-slate-800">
      {/* Header */}
      <div className="p-4 border-b border-slate-800/50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <Home className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">
              {role === 'partner-construction' ? 'Construction' : 'Interior'}
            </p>
            <p className="text-xs text-slate-400 truncate max-w-[150px]">
              {partnerName || 'Partner'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-800/50">
          <span className="text-xs text-slate-400">
            {assignedSites?.length || 0} site(s) assigned
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-3 space-y-1">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.id}
              to={item.path}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                active
                  ? 'bg-amber-500/10 text-amber-400 border-r-2 border-amber-400'
                  : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
              )}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span>{item.label}</span>
            </Link>
          );
        })}

        {/* Read-only badge */}
        <div className="mt-6 pt-4 border-t border-slate-700/50">
          <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400">
            <Eye className="w-4 h-4 flex-shrink-0" />
            <span>Read-only access</span>
          </div>
        </div>

        {/* Logout */}
        <div className="mt-2 pt-2 border-t border-slate-700/50">
          <button
            onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-200"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span>Logout</span>
          </button>
        </div>
      </nav>
    </aside>
  );
};

// ─── Main PartnerLayout ──────────────────────────────────────────
export default function PartnerLayout() {
  const { user, isAuthenticated, loading } = useAuth();
  const [partnerName, setPartnerName] = useState('');
  const [role, setRole] = useState('');
  const [assignedSites, setAssignedSites] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchPartnerData = async () => {
      if (!user) {
        setLoadingData(false);
        return;
      }

      try {
        const partners = await pb.collection('partners').getFullList({
          filter: `createdBy = "${user.id}" || email = "${user.email}"`,
        });

        if (partners.length === 0) {
          setLoadingData(false);
          return;
        }

        const partner = partners[0];
        setPartnerName(partner.name || user.name || 'Partner');
        setRole(user.role || 'partner-construction');

        const assignments = await pb.collection('partner_site_assignments').getFullList({
          filter: `partner = "${partner.id}" && isActive = true`,
          expand: 'site',
        });

        const sites = assignments.map(a => a.expand?.site).filter(Boolean);
        setAssignedSites(sites);
      } catch (error) {
        console.error('Error fetching partner data:', error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchPartnerData();
  }, [user]);

  if (loading || loadingData) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 animate-spin text-amber-500" />
          <p className="text-slate-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isPartner = user?.role === 'partner-construction' || user?.role === 'partner-interior';
  if (!isPartner) {
    return <Navigate to="/dashboard/main" replace />;
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200/60 shadow-sm flex-shrink-0 z-30">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            <div className="flex items-center gap-3">
              <img 
                src="https://files.housedesigns99.com/api/files/f5eolh49d789mqi/rwth7jtsmlcst1n/house_designs99_logo_fwjsybt4i7.png"
                alt="House Designs 99"
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  const parent = e.target.parentElement;
                  if (parent) {
                    const fallback = document.createElement('span');
                    fallback.className = 'text-xl font-bold text-amber-500';
                    fallback.textContent = 'HD99';
                    parent.appendChild(fallback);
                  }
                }}
              />
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <span className="text-amber-500">🏗️</span>
                  Partner Panel
                </h1>
                <p className="text-xs text-slate-500">
                  {role === 'partner-construction' ? 'Construction' : 'Interior'} Partner
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:block">{partnerName}</span>
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <span className="text-sm font-bold text-amber-700">
                {partnerName?.charAt(0)?.toUpperCase() || 'P'}
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className={cn(
          'fixed inset-y-0 left-0 z-40 w-64 bg-slate-900 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 flex-shrink-0 h-full overflow-y-auto',
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}>
          <PartnerSidebar
            partnerName={partnerName}
            role={role}
            assignedSites={assignedSites}
          />
        </div>

        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50/50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
